import ExtensionCollection from './ExtensionCollection';
import { Autopopulate } from './QuestionnaireMapper/QuestionMappers/FormValueMappers/AutopopulationValueFormValueMapper';
import surveyUrls from './surveyUrls';

export type AutopopulateSourceResourceType =
  | 'Patient'
  | 'Condition'
  | 'Observation'
  | 'Encounter'
  | 'Procedure'
  | 'Previous_Response';

export type AutopopulateQueryParam = {
  field: string;
  value: string;
};

export const VALID_RESOURCE_TYPES: AutopopulateSourceResourceType[] = [
  'Patient',
  'Condition',
  'Observation',
  'Encounter',
  'Procedure',
  'Previous_Response',
];
const PARAM_DELIMITER = '__|__';
const KEY_VALUE_DELIMITER = '__:__';

const isValidQueryParam = (param: AutopopulateQueryParam) =>
  !!param?.field && !!param?.value;

function parseQueryParamsString(
  queryParamsString: string,
): AutopopulateQueryParam[] {
  return (queryParamsString || '')
    .split(PARAM_DELIMITER)
    .map(_ => {
      const [field, value] = (_ || '').split(KEY_VALUE_DELIMITER);
      return { field, value };
    })
    .filter(isValidQueryParam);
}

function queryParamsToString(params: AutopopulateQueryParam[]): string {
  return (params || [])
    .filter(isValidQueryParam)
    .map(_ => [_.field, _.value].join(KEY_VALUE_DELIMITER))
    .join(PARAM_DELIMITER);
}

export default class SurveyItemAutopopulation {
  static initFromFormValues(
    formValues: Autopopulate,
  ): SurveyItemAutopopulation {
    return new SurveyItemAutopopulation(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      formValues.autopopulateSourceResourceType,
      formValues.autopopulateQueryParams,
      formValues.autopopulateValueSelector,
    );
  }

  static initFromQuestionnaireItem(
    questionnaireItem: fhir.QuestionnaireItem,
  ): SurveyItemAutopopulation {
    const extensionCollection =
      ExtensionCollection.initFromBackboneElement(questionnaireItem);
    const resourceType = extensionCollection.getValueString(
      surveyUrls.autopopulateValues.resourceType,
    );
    const rawQueryParams = extensionCollection.getValueString(
      surveyUrls.autopopulateValues.queryParams,
    );
    const queryParams = parseQueryParamsString(rawQueryParams);
    const valueSelector = extensionCollection.getValueString(
      surveyUrls.autopopulateValues.valueSelector,
    );
    return new SurveyItemAutopopulation(
      resourceType as AutopopulateSourceResourceType,
      queryParams,
      valueSelector,
    );
  }

  constructor(
    public readonly resourceType: AutopopulateSourceResourceType,
    public readonly queryParams: AutopopulateQueryParam[],
    public readonly valueSelector: string,
  ) {}

  public get isValid(): boolean {
    return (
      !!this.resourceType &&
      (this.resourceType === 'Previous_Response' || !!this.valueSelector) &&
      VALID_RESOURCE_TYPES.includes(this.resourceType)
    );
  }

  public get hasQueryParams(): boolean {
    return !!this.queryParams.length;
  }

  public get queryParamsExtensionValueString(): string {
    return queryParamsToString(this.queryParams);
  }
}
